import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  AuctionTimerType,
  NetworkVariant,
  TradeContext,
  TradeStateNew,
  TradeStateNewName,
  TradeContextName,
  TradeViewVariant
} from '../../../_shared/types/trade';
import { localStorageService } from '../../../services/StorageService';
import LOCALSTORAGE from '../../../_shared/constants/localStorage';
import VDP_REDIRECT_PARAM from '../../../_shared/constants/redirectTabType';
import { updateSelectedTrade } from '../../../redux/slices/ui/selectedTrades';
import ROUTES from '../../Routes/constants';
import Content from './Content';
import ActiveTimer from '../../../_shared/components/ActiveTimer';
import {
  StyledPendingInvalidTradeCard,
  TradeContentContainer
} from '../PendingNegotiationCard/Styles';
import ReducerConstants from '../../../_shared/constants/reducers';
import DealerInfo from '../ListTradeCard/Content/DealerInfo';
import NetworkInfo from '../ListTradeCard/Content/NetworkInfo';
import {
  StyledActiveListTimer,
  StyledPendingThumbnail,
  StyledRow,
  StyledWrapper
} from './Styles';
import { NetworkName } from '../ListTradeCard/Content/DealerInfo/Styles';
import scrollToTop from '../../../_shared/helpers/scrollToTop';

const PendingTradeCardContent = ({
  testId,
  tradeSummary,
  tradeState,
  tradeContext,
  listCardVariant,
  onAuctionEnded,
  isOmp = false,
  handleNavigateToTradeUri
}) => {
  const {
    dealer,
    isGreyOut,
    sellingNetworkId,
    isInternalTrade,
    associatedTradeId,
    id,
    thumb
  } = tradeSummary;

  const [isAuctionEnded, setIsAucionEnded] = useState(isGreyOut);
  const history = useHistory();
  const dispatch = useDispatch();
  const pendingExpiry = tradeSummary?.datePendingExpiry;
  const user = useSelector(state => state?.auth?.user, shallowEqual);
  const isCsu = user?.superUserId && user?.superUserId !== user?.id;

  const [sellingNetworks] = useSelector(
    state => [
      state[ReducerConstants.AUTH]?.user?.auctionSource?.sellingNetworks
    ],
    shallowEqual
  );
  const [pendingCardFooter, setPendingCardFooter] = useState(false);

  // Mapping networks by networkId, i.e. [{"id": ...}] -> {"123": ..., "234": ...}
  const network =
    sellingNetworks &&
    sellingNetworks.reduce((obj, item) => {
      obj[item.id] = item;
      return obj;
    }, {})[sellingNetworkId];

  // Buyer: show dealer ratings, DGN name if present. Only accessible to CSU after negotiation ends.
  const showBuyerNetworkFooter = isGreyOut
    ? isCsu
    : tradeContext === TradeContext.Buy && network;

  // Seller: "Open Network", "DGN name", or not at all.
  const showSellerNetworkFooter =
    tradeContext === TradeContext.Sell &&
    (isInternalTrade ||
      pendingCardFooter ||
      network?.isDealerGroupNetwork ||
      (network?.isOpenNetwork && associatedTradeId !== null));

  const onSelect = tradeId => {
    localStorageService.setItem(LOCALSTORAGE.BUY_TRADE, tradeId);
    localStorageService.setItem(LOCALSTORAGE.PENDING_TRADE, tradeId);
    dispatch(
      updateSelectedTrade({
        tradeState,
        tradeId
      })
    );
    const tradeDetailsPath = `${ROUTES.TRADE_URI}${tradeId}${
      isOmp
        ? `?tab=${
            tradeContext === TradeContext.Sell
              ? VDP_REDIRECT_PARAM.SELL_PENDING
              : VDP_REDIRECT_PARAM.BUY_PENDING
          }`
        : ''
    }`;

    if (handleNavigateToTradeUri) {
      handleNavigateToTradeUri(tradeDetailsPath, {
        state: {
          state: TradeStateNewName[TradeStateNew.PENDING],
          context: TradeContextName[tradeContext]
        }
      });
    } else {
      history.push(tradeDetailsPath);
    }
    scrollToTop(isOmp);
  };

  const onCardClick = e => {
    if (e?.ctrlKey || e?.metaKey) {
      return false;
    }

    onSelect(id);
    return true;
  };

  const getFooterByContext = context => {
    switch (context) {
      case TradeContext.Buy:
        return (
          <DealerInfo
            dealer={dealer}
            network={network}
            showIconOnly
            showBadge={false}
          />
        );
      case TradeContext.Sell:
        if (
          (network?.isOpenNetwork && associatedTradeId) ||
          pendingCardFooter
        ) {
          // "Open Network"
          return (
            <NetworkName variant={NetworkVariant.PENDING}>
              {network?.displayName}
            </NetworkName>
          );
        }
        if (isInternalTrade) {
          // "Network Star Dealer Group Network (Duplicate in Open network)"
          return (
            <NetworkInfo
              displayName={network?.displayName}
              variant={NetworkVariant.PENDING}
            />
          );
        }
        if (network?.isDealerGroupNetwork) {
          // "Network Star Dealer Group Network"
          return (
            <NetworkInfo
              displayName={network?.displayName}
              variant={NetworkVariant.PENDING}
              hasNetworkLabel
            />
          );
        }
        return null;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (isAuctionEnded && onAuctionEnded) {
      onAuctionEnded();
    }
  }, [isAuctionEnded, onAuctionEnded]);

  return (
    <TradeContentContainer>
      <StyledRow
        testId={`${testId}-content`}
        showBottomBorder={!showSellerNetworkFooter && !showBuyerNetworkFooter}
      >
        <StyledPendingThumbnail url={thumb} onClick={e => onCardClick(e)}>
          <StyledActiveListTimer>
            <ActiveTimer
              onAuctionEnded={() => {
                setIsAucionEnded(true);
              }}
              activeExpiryTime={pendingExpiry}
              location={TradeViewVariant.LIST}
              tradeId={id}
              tradeCardAuctionEnded={isAuctionEnded}
              variant={AuctionTimerType.PENDING}
            />
          </StyledActiveListTimer>
        </StyledPendingThumbnail>
        <Content
          testId={testId}
          tradeSummary={tradeSummary}
          onCardClick={e => onCardClick(e)}
          tradeContext={tradeContext}
          sellingNetworks={sellingNetworks}
          isOpenNetwork={network?.isOpenNetwork}
          setPendingCardFooter={setPendingCardFooter}
          listCardVariant={listCardVariant}
          isOmp={isOmp}
        />

        {isAuctionEnded && (
          <StyledPendingInvalidTradeCard testId={`${testId}-pending-ended`} />
        )}
      </StyledRow>
      {(showBuyerNetworkFooter || showSellerNetworkFooter) && (
        <StyledWrapper>{getFooterByContext(tradeContext)}</StyledWrapper>
      )}
    </TradeContentContainer>
  );
};
export default PendingTradeCardContent;
